import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi, ApiError } from '../lib/api';
import Wordmark from '../components/Wordmark';
import Eyebrow from '../components/Eyebrow';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/Button';

type Stage =
  | { kind: 'loading' }
  | { kind: 'idle'; enabled: boolean }
  | { kind: 'setup'; secret: string; otpauthUrl: string }
  | { kind: 'disable' };

export default function AdminSecurity() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const s = await authApi.totpStatus();
      setStage({ kind: 'idle', enabled: s.enabled });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError('Could not load two-factor status.');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function startSetup() {
    setError(null);
    try {
      const r = await authApi.totpSetup();
      setStage({ kind: 'setup', secret: r.secret, otpauthUrl: r.otpauth_url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start setup.');
    }
  }

  return (
    <main className="min-h-screen bg-page text-fg">
      <header className="border-b border-line bg-page/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3 text-muted hover:text-fg transition-colors">
            <span aria-hidden>←</span>
            <Wordmark size="sm" />
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint border-l border-line pl-3 ml-1">
              Security
            </span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Eyebrow>// Two-factor authentication</Eyebrow>
        <h1 className="font-display font-bold text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-4">
          Two-factor authentication
        </h1>
        <p className="text-muted leading-[1.55] max-w-prose mb-10">
          Adds a second factor to admin sign-in. After your password, you'll be asked for a 6-digit
          code from your authenticator app (Google Authenticator, 1Password, Bitwarden, etc).
        </p>

        {stage.kind === 'loading' && (
          <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
        )}

        {stage.kind === 'idle' && !stage.enabled && (
          <div className="bg-card border border-line rounded-md p-6 md:p-8">
            <p className="text-fg text-[15px] leading-[1.55] mb-4">
              Two-factor is currently <span className="text-muted">off</span>. Enable it to require a
              code from your authenticator app every time you sign in.
            </p>
            <Button variant="primary" glow onClick={startSetup}>
              Enable two-factor
            </Button>
          </div>
        )}

        {stage.kind === 'idle' && stage.enabled && (
          <div className="bg-card border border-line rounded-md p-6 md:p-8">
            <p className="text-fg text-[15px] leading-[1.55] mb-4">
              Two-factor is <span className="text-accent">on</span>. You'll be asked for a 6-digit
              code on every sign-in.
            </p>
            <p className="text-faint text-[13px] leading-[1.55] mb-6">
              Lost your authenticator? Disabling requires your password and a current code. If you
              don't have either, you can clear <code className="font-mono text-[12px]">totp_secret_b32</code>{' '}
              and <code className="font-mono text-[12px]">totp_enabled_at</code> on the{' '}
              <code className="font-mono text-[12px]">admin</code> row directly via{' '}
              <code className="font-mono text-[12px]">wrangler d1 execute</code>.
            </p>
            <Button variant="ghost" onClick={() => setStage({ kind: 'disable' })}>
              Disable two-factor
            </Button>
          </div>
        )}

        {stage.kind === 'setup' && (
          <SetupCard
            secret={stage.secret}
            otpauthUrl={stage.otpauthUrl}
            onCancel={() => void refresh()}
            onEnabled={() => void refresh()}
          />
        )}

        {stage.kind === 'disable' && (
          <DisableCard
            onCancel={() => void refresh()}
            onDisabled={() => void refresh()}
          />
        )}

        {error && (
          <p className="text-accent text-[13px] mt-6" role="alert">
            {error}
          </p>
        )}

        <BackupPanel />

        <ErrorLogPanel />
      </section>
    </main>
  );
}

// ---------- Manual backup ----------

function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      await authApi.runBackup();
      setResult({
        ok: true,
        message:
          'Backup written to R2. Check the hsp-scripts-backups bucket — newest object is under weekly/.',
      });
    } catch (e) {
      setResult({
        ok: false,
        message: e instanceof Error ? e.message : 'Backup failed.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-16">
      <Eyebrow>// Backups</Eyebrow>
      <h2 className="font-display font-bold text-[24px] tracking-[-0.01em] mt-2 mb-3">
        Database backups
      </h2>
      <p className="text-muted text-[13px] leading-[1.55] mb-6 max-w-prose">
        D1 → R2 backups run automatically every Sunday at 02:00 UTC and keep
        the last 8 weekly snapshots (~2 months). Use the button below to take
        a manual snapshot right now — useful before a risky migration, or
        just to verify the pipeline is working.
      </p>

      <div className="bg-card border border-line rounded-md p-6 md:p-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-fg text-[14px] leading-[1.55]">
              Take a fresh snapshot now.
            </p>
            <p className="text-faint text-[12px] mt-1">
              Writes a gzipped JSON dump to R2 under{' '}
              <code className="font-mono text-[11px]">weekly/YYYY-MM-DD_HH00.json.gz</code>.
            </p>
          </div>
          <Button variant="secondary" onClick={run} disabled={busy}>
            {busy ? 'Running…' : 'Run backup now'}
          </Button>
        </div>
        {result && (
          <p
            className={`text-[13px] mt-4 ${result.ok ? 'text-accent' : 'text-accent'}`}
            role="alert"
          >
            {result.ok ? '✓ ' : '✗ '}
            {result.message}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------- Recent errors ----------

interface ErrorRow {
  id: number;
  created_at: number;
  source: string;
  message: string;
  stack: string | null;
  context: string | null;
}

function ErrorLogPanel() {
  const [errors, setErrors] = useState<ErrorRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const res = await authApi.listErrors();
      setErrors(res.errors);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load errors.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <section className="mt-16">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Eyebrow>// Recent errors</Eyebrow>
          <h2 className="font-display font-bold text-[24px] tracking-[-0.01em] mt-2">
            Production errors
          </h2>
        </div>
        <Button variant="ghost" onClick={refresh} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <p className="text-muted text-[13px] leading-[1.55] mb-6 max-w-prose">
        Server-side errors recorded by the worker — webhook delivery failures,
        unexpected exceptions, etc. Auto-purges after 30 days. If something
        looks off in the system, this is the first place to check.
      </p>

      {err && (
        <p className="text-accent text-[13px] mb-4" role="alert">
          {err}
        </p>
      )}

      {errors === null ? null : errors.length === 0 ? (
        <div className="border border-dashed border-line rounded-md px-6 py-10 text-center bg-card">
          <p className="text-faint text-[13px]">No errors recorded. Quiet is good.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {errors.map((e) => (
            <li key={e.id} className="bg-card border border-line rounded-md">
              <button
                type="button"
                onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                className="w-full text-left px-4 py-3 flex items-start justify-between gap-4 hover:bg-elevated/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent">
                      {e.source}
                    </span>
                    <span className="font-mono text-[10px] text-faint">
                      {formatTimestamp(e.created_at)}
                    </span>
                  </div>
                  <p className="text-[13px] text-fg break-words">{e.message}</p>
                </div>
                <span className="text-faint text-[11px] shrink-0 mt-0.5">
                  {expanded === e.id ? '−' : '+'}
                </span>
              </button>
              {expanded === e.id && (e.stack || e.context) && (
                <div className="border-t border-line px-4 py-3 space-y-2 bg-elevated/30">
                  {e.context && (
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted mb-1">
                        Context
                      </p>
                      <pre className="text-[11px] text-muted whitespace-pre-wrap break-all font-mono">
                        {e.context}
                      </pre>
                    </div>
                  )}
                  {e.stack && (
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted mb-1">
                        Stack
                      </p>
                      <pre className="text-[11px] text-muted whitespace-pre-wrap break-all font-mono">
                        {e.stack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SetupCard({
  secret,
  otpauthUrl,
  onCancel,
  onEnabled,
}: {
  secret: string;
  otpauthUrl: string;
  onCancel: () => void;
  onEnabled: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.totpEnable(code);
      onEnabled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify code.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-line rounded-md p-6 md:p-8">
      <h2 className="font-display font-semibold text-[20px] tracking-[-0.01em] mb-2">
        Add to your authenticator app
      </h2>
      <p className="text-muted text-[14px] leading-[1.55] mb-6">
        In your authenticator app, choose <strong className="text-fg">Add account</strong> →{' '}
        <strong className="text-fg">Enter a setup key</strong> and paste the secret below. Then
        enter the first 6-digit code your app shows to confirm.
      </p>

      <label className="block">
        <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
          Setup key
        </span>
        <div className="flex gap-2">
          <code className="flex-1 px-4 py-3 bg-elevated border border-line rounded-sm font-mono text-[14px] tracking-[0.08em] break-all select-all">
            {formatSecret(secret)}
          </code>
          <Button
            type="button"
            variant="ghost"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(secret);
                setSecretCopied(true);
                setTimeout(() => setSecretCopied(false), 1500);
              } catch {
                // ignore
              }
            }}
          >
            {secretCopied ? '✓ Copied' : 'Copy'}
          </Button>
        </div>
      </label>

      <details className="mt-4">
        <summary className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted cursor-pointer hover:text-fg transition-colors">
          Show otpauth:// URL (advanced)
        </summary>
        <code className="block mt-2 px-4 py-3 bg-elevated border border-line rounded-sm font-mono text-[12px] break-all select-all">
          {otpauthUrl}
        </code>
      </details>

      <label className="block mt-6">
        <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
          6-digit code from your app
        </span>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="123456"
          className="w-full px-4 py-3 bg-elevated border border-line rounded-sm text-fg text-[15px] focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
        />
      </label>

      {error && (
        <p className="text-accent text-[13px] mt-4" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 mt-8">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" glow disabled={busy || code.length !== 6}>
          {busy ? 'Verifying…' : 'Verify and enable'}
        </Button>
      </div>
    </form>
  );
}

function DisableCard({ onCancel, onDisabled }: { onCancel: () => void; onDisabled: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.totpDisable(password, code);
      onDisabled();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disable two-factor.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-line rounded-md p-6 md:p-8">
      <h2 className="font-display font-semibold text-[20px] tracking-[-0.01em] mb-2">
        Disable two-factor
      </h2>
      <p className="text-muted text-[14px] leading-[1.55] mb-6">
        Confirm your password and a current code from your authenticator app.
      </p>

      <label className="block">
        <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
          Password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="w-full px-4 py-3 bg-elevated border border-line rounded-sm text-fg text-[15px] focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
        />
      </label>

      <label className="block mt-4">
        <span className="block font-mono text-[11px] uppercase tracking-[0.08em] text-muted mb-2">
          6-digit code
        </span>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          className="w-full px-4 py-3 bg-elevated border border-line rounded-sm text-fg text-[15px] focus:border-accent focus:shadow-focus focus:outline-none transition-all duration-150"
        />
      </label>

      {error && (
        <p className="text-accent text-[13px] mt-4" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 mt-8">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={busy || !password || code.length !== 6}>
          {busy ? 'Disabling…' : 'Disable two-factor'}
        </Button>
      </div>
    </form>
  );
}

// Group the base32 secret in 4-char blocks for readability.
function formatSecret(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}
